import {
  createDatabaseShardManifest,
  createDatabaseShardManifestEntry,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { WriterInfoProvider } from '@blocksuite/affine-shared/services';
import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  createDatabaseRowCreationSystemFieldCells,
  createDatabaseRowUpdateSystemFieldCells,
  DATABASE_LARGE_DATA_POLICY,
  DATABASE_ROW_CREATED_BY_FIELD_ID,
  DATABASE_ROW_CREATED_TIME_FIELD_ID,
  type DatabaseRowSystemFieldCells,
  DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
} from '@streamlain/database-identity';
import { encodeStateAsUpdate, Map as YMap } from 'yjs';

import {
  type DatabaseRowCellValueInsert,
  type DataSourceWriteActor,
  type RawValueJsonConverter,
  type RowShardCell,
  type RowShardCells,
  type WritableRowShardMap,
} from './data-source-contracts.js';
import type {
  DatabaseShardDocument,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  encodedJsonBytes,
  FILE_INDEX_SHARD_WRITE_OVERHEAD_BYTES,
  fromJsonValue,
  isReadonlyYMap,
  normalizeRowShardCell,
  SCHEMA_LOCKED_INVALID_CELL_VALUE,
} from './data-source-values.js';
import {
  cloneDatabaseShardManifest,
  type DatabaseSystemDocAccessRegistrationPayload,
  registerDatabaseSystemDocAccessBindingRequest,
} from './database-system-doc-access-registration.js';
import {
  getFileIndexShardRegistry,
  getFileIndexWriterShardPrefix,
  nextFileIndexWriterShardId,
  projectFileIndexShardEntries,
  selectFileIndexWriterShard,
  sortFileIndexShardEntries,
} from './file-index-shard-registry.js';
import { touchRowMeta } from './utils/block-utils.js';

export function createEmptyShardManifest(
  this: DatabaseSourceRuntime
): DatabaseShardManifest {
  return createDatabaseShardManifest({
    databaseId: this.databaseId,
    databaseRootDocId: this._model.store.id,
    workspaceId: this._model.store.workspace.id,
    shards: [],
  });
}

export function initializeFileIndexShardRegistry(
  this: DatabaseSourceRuntime
): void {
  if (!this.isFileIndexDatabase()) return;
  const registry = getFileIndexShardRegistry(
    this._model.store.doc.spaceDoc,
    this.databaseId
  );
  const manifest =
    normalizeDatabaseShardManifest(this._model.props.databaseShardManifest) ??
    this.createEmptyShardManifest();
  const seededEntries = new Map(registry.entries());
  for (const shard of manifest.shards) {
    if (!seededEntries.has(shard.shardId)) {
      seededEntries.set(shard.shardId, shard);
    }
  }
  const seededShards = sortFileIndexShardEntries(seededEntries.values());
  this.assertFileIndexRootSchemaWithinLimits({
    manifest: createDatabaseShardManifest({
      ...manifest,
      shards: seededShards,
    }),
    registryShards: seededShards,
  });
  this._model.store.doc.spaceDoc.transact(() => {
    for (const shard of seededShards) {
      if (!registry.has(shard.shardId)) {
        registry.set(shard.shardId, shard);
      }
    }
  });

  const reconcile = () => {
    if (
      this._disposed ||
      this._publishingFileIndexShardManifest ||
      this._fileIndexManifestReconcileQueued
    ) {
      return;
    }
    this._fileIndexManifestReconcileQueued = true;
    queueMicrotask(() => {
      this._fileIndexManifestReconcileQueued = false;
      if (!this._disposed) this.publishLocalShardManifest([]);
    });
  };
  registry.observe(reconcile);
  this._fileIndexShardRegistryDisposer = () => registry.unobserve(reconcile);
  this.publishLocalShardManifest([]);
}

export function publishLocalShardManifest(
  this: DatabaseSourceRuntime,
  shards: DatabaseShardManifestEntry[]
): void {
  const previous =
    normalizeDatabaseShardManifest(this._model.props.databaseShardManifest) ??
    this.createEmptyShardManifest();
  let canonicalShards = shards;
  const registry = this.isFileIndexDatabase()
    ? getFileIndexShardRegistry(this._model.store.doc.spaceDoc, this.databaseId)
    : undefined;
  if (registry) {
    canonicalShards = projectFileIndexShardEntries(registry.values(), shards);
  }
  const manifest = createDatabaseShardManifest({
    databaseId: previous.databaseId,
    databaseRootDocId: previous.databaseRootDocId,
    workspaceId: previous.workspaceId,
    targetBytes: previous.targetBytes,
    hardLimitBytes: this.isFileIndexDatabase()
      ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      : previous.hardLimitBytes,
    shards: canonicalShards,
  });
  this.assertFileIndexRootSchemaWithinLimits({
    manifest,
    registryShards: canonicalShards,
  });
  this._publishingFileIndexShardManifest = true;
  try {
    if (registry) {
      this._model.store.doc.spaceDoc.transact(() => {
        for (const shard of canonicalShards) {
          if (
            JSON.stringify(registry.get(shard.shardId)) !==
            JSON.stringify(shard)
          ) {
            registry.set(shard.shardId, shard);
          }
        }
      });
    }
    if (JSON.stringify(previous) !== JSON.stringify(manifest)) {
      this.storageDoc.transact(() => {
        this._model.props.databaseShardManifest = manifest;
      });
    }
  } finally {
    this._publishingFileIndexShardManifest = false;
  }
  this.registerSystemDocAccessBinding();
}

export function registerSystemDocAccessBinding(
  this: DatabaseSourceRuntime,
  attempt = 0
): void {
  if (this._disposed) {
    return;
  }
  if (typeof window === 'undefined' || typeof fetch !== 'function') {
    return;
  }

  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) return;

  const workspaceId = this._hostModel.store.workspace.id;
  const accessDocId = this._hostModel.store.id;
  const rootDocId = manifest.databaseRootDocId;
  if (!workspaceId || accessDocId === rootDocId) return;

  const payload: DatabaseSystemDocAccessRegistrationPayload = {
    workspaceId,
    databaseId: manifest.databaseId,
    rootDocId,
    accessDocId,
    blockId: this._hostModel.id,
    manifest: cloneDatabaseShardManifest(manifest),
  };
  registerDatabaseSystemDocAccessBindingRequest(payload, {
    attempt,
    fetchImpl: fetch,
    isDisposed: () => this._disposed,
  });
}

export function createLocalShardEntry(
  this: DatabaseSourceRuntime,
  shardId: string
): DatabaseShardManifestEntry {
  return createDatabaseShardManifestEntry({
    databaseId: this.databaseId,
    workspaceId: this._model.store.workspace.id,
    shardId,
    firstRowId: undefined,
    lastRowId: undefined,
    rowCount: 0,
    encodedBytes: 0,
    hardLimitBytes: this.isFileIndexDatabase()
      ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      : DATABASE_LARGE_DATA_POLICY.shard.hardLimitBytes,
    status: 'ready',
  });
}

export function updateLocalShardManifestEntry(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  rowIds: string[],
  encodedBytes: number
): DatabaseShardManifestEntry {
  return createDatabaseShardManifestEntry({
    databaseId: this.databaseId,
    workspaceId: this._model.store.workspace.id,
    shardId: shard.shardId,
    firstRowId: rowIds[0],
    lastRowId: rowIds.at(-1),
    rowCount: rowIds.length,
    encodedBytes,
    hardLimitBytes: this.isFileIndexDatabase()
      ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      : DATABASE_LARGE_DATA_POLICY.shard.hardLimitBytes,
    status: 'ready',
  });
}

export function ensureWritableShardForInsert(
  this: DatabaseSourceRuntime,
  index: number,
  estimatedAdditionalBytes = 0
): {
  shard: DatabaseShardManifestEntry;
  shardIndex: number;
  localIndex: number;
  shardDoc: DatabaseShardDocument;
} {
  const manifest =
    normalizeDatabaseShardManifest(this._model.props.databaseShardManifest) ??
    this.createEmptyShardManifest();
  let shards = manifest.shards;
  if (shards.length === 0) {
    const shard = this.createLocalShardEntry(
      this.isFileIndexDatabase()
        ? nextFileIndexWriterShardId(this._model.store.doc.spaceDoc, shards)
        : '00001'
    );
    shards = [shard];
    if (!this.isFileIndexDatabase()) this.publishLocalShardManifest(shards);
  }

  if (this.isFileIndexDatabase()) {
    const writerPrefix = getFileIndexWriterShardPrefix(
      this._model.store.doc.spaceDoc
    );
    let writerShard = selectFileIndexWriterShard(
      shards,
      writerPrefix,
      this._fileIndexWriterShardId,
      manifest.targetBytes
    );
    if (!writerShard) {
      writerShard = this.createLocalShardEntry(
        nextFileIndexWriterShardId(this._model.store.doc.spaceDoc, shards)
      );
      shards = [...shards, writerShard];
    }
    this._fileIndexWriterShardId = writerShard.shardId;
    const writerShardDoc = this.ensureShardObserved(writerShard, 'write');
    if (
      writerShard.rowCount > 0 &&
      encodeStateAsUpdate(writerShardDoc.spaceDoc).byteLength +
        estimatedAdditionalBytes >=
        manifest.targetBytes
    ) {
      const shard = this.createLocalShardEntry(
        nextFileIndexWriterShardId(this._model.store.doc.spaceDoc, shards)
      );
      shards = [...shards, shard];
      this._fileIndexWriterShardId = shard.shardId;
      return {
        shard,
        shardIndex: shards.length - 1,
        localIndex: 0,
        shardDoc: this.ensureShardObserved(shard, 'write'),
      };
    }
    return {
      shard: writerShard,
      shardIndex: shards.findIndex(
        shard => shard.shardId === writerShard.shardId
      ),
      localIndex: writerShard.rowCount,
      shardDoc: writerShardDoc,
    };
  }

  const isAppend = index >= this.getManifestRowCount(manifest);
  const lastShard = shards.at(-1)!;
  if (isAppend && lastShard.rowCount > 0) {
    const lastShardDoc = this.ensureShardObserved(lastShard, 'write');
    if (
      encodeStateAsUpdate(lastShardDoc.spaceDoc).byteLength +
        estimatedAdditionalBytes >=
      manifest.targetBytes
    ) {
      const nextShardNumber =
        Math.max(
          0,
          ...shards.map(shard => Number.parseInt(shard.shardId, 10) || 0)
        ) + 1;
      const shard = this.createLocalShardEntry(
        String(nextShardNumber).padStart(5, '0')
      );
      shards = [...shards, shard];
      this.publishLocalShardManifest(shards);
      return {
        shard,
        shardIndex: shards.length - 1,
        localIndex: 0,
        shardDoc: this.ensureShardObserved(shard, 'write'),
      };
    }
  }

  let shardStart = 0;
  for (let shardIndex = 0; shardIndex < shards.length; shardIndex++) {
    const shard = shards[shardIndex]!;
    const shardEnd = shardStart + shard.rowCount;
    const isLastShard = shardIndex === shards.length - 1;
    if (index < shardEnd || isLastShard) {
      return {
        shard,
        shardIndex,
        localIndex: Math.max(0, Math.min(index - shardStart, shard.rowCount)),
        shardDoc: this.ensureShardObserved(shard, 'write'),
      };
    }
    shardStart = shardEnd;
  }

  const shard = shards.at(-1)!;
  return {
    shard,
    shardIndex: shards.length - 1,
    localIndex: shard.rowCount,
    shardDoc: this.ensureShardObserved(shard, 'write'),
  };
}

export function findWritableShardForRow(
  this: DatabaseSourceRuntime,
  rowId: string
): {
  shard: DatabaseShardManifestEntry;
  shardIndex: number;
  rowIndex: number;
  shardDoc: DatabaseShardDocument;
} {
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database "${this.databaseId}" has no row-shard manifest.`
    );
  }

  for (let shardIndex = 0; shardIndex < manifest.shards.length; shardIndex++) {
    const shard = manifest.shards[shardIndex]!;
    const shardDoc = this.ensureShardObserved(shard, 'write');
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const rowIds = rowOrder.toArray().map(id => String(id));
    const rowIndex = rowIds.indexOf(rowId);
    if (rowIndex >= 0) {
      return { shard, shardIndex, rowIndex, shardDoc };
    }
  }

  throw new BlockSuiteError(
    ErrorCode.DatabaseBlockError,
    `Row "${rowId}" does not exist in sharded database "${this.databaseId}".`
  );
}

export function updateLocalShardEntryAtIndex(
  this: DatabaseSourceRuntime,
  shardIndex: number,
  shard: DatabaseShardManifestEntry,
  rowIds: string[]
): void {
  const manifest =
    normalizeDatabaseShardManifest(this._model.props.databaseShardManifest) ??
    this.createEmptyShardManifest();
  const shards = manifest.shards.slice();
  if (rowIds.length === 0) {
    if (this.isFileIndexDatabase()) {
      getFileIndexShardRegistry(
        this._model.store.doc.spaceDoc,
        this.databaseId
      ).delete(shard.shardId);
      if (this._fileIndexWriterShardId === shard.shardId) {
        this._fileIndexWriterShardId = undefined;
      }
    }
    if (shardIndex >= 0) shards.splice(shardIndex, 1);
    this.publishLocalShardManifest(this.isFileIndexDatabase() ? [] : shards);
  } else {
    const shardDoc = this.ensureShardObserved(shard, 'write');
    const nextShard = this.updateLocalShardManifestEntry(
      shard,
      rowIds,
      encodeStateAsUpdate(shardDoc.spaceDoc).byteLength
    );
    if (shardIndex >= 0) shards[shardIndex] = nextShard;
    else shards.push(nextShard);
    this.publishLocalShardManifest(
      this.isFileIndexDatabase() ? [nextShard] : shards
    );
  }
}

export function currentWriterId(this: DatabaseSourceRuntime): string {
  return (
    this._model.store.getOptional(WriterInfoProvider)?.getWriterInfo()?.id ?? ''
  );
}

export function writeShardedSystemFieldCells(
  this: DatabaseSourceRuntime,
  rowMap: WritableRowShardMap,
  cells: DatabaseRowSystemFieldCells
): void {
  for (const [columnId, cell] of Object.entries(cells)) {
    if (cell) {
      rowMap.set(columnId, cell);
    }
  }
}

export function initializeShardedRowSystemFields(
  this: DatabaseSourceRuntime,
  rowMap: WritableRowShardMap
): void {
  this.writeShardedSystemFieldCells(
    rowMap,
    createDatabaseRowCreationSystemFieldCells({
      now: Date.now(),
      userId: this.currentWriterId(),
    })
  );
}

export function readShardedSystemFieldValue(
  this: DatabaseSourceRuntime,
  rowMap: WritableRowShardMap,
  rowId: string,
  fieldId: string
): unknown {
  const rowMapValue = normalizeRowShardCell(fieldId, rowMap.get(fieldId)).value;
  if (rowMapValue !== undefined && rowMapValue !== null) {
    return rowMapValue;
  }
  return this._shardedRowsById.get(rowId)?.cells[fieldId]?.value;
}

export function touchShardedRowSystemFields(
  this: DatabaseSourceRuntime,
  rowId: string,
  rowMap: WritableRowShardMap
): void {
  this.writeShardedSystemFieldCells(
    rowMap,
    createDatabaseRowUpdateSystemFieldCells({
      now: Date.now(),
      userId: this.currentWriterId(),
      createdTimeValue: this.readShardedSystemFieldValue(
        rowMap,
        rowId,
        DATABASE_ROW_CREATED_TIME_FIELD_ID
      ),
      createdByValue: this.readShardedSystemFieldValue(
        rowMap,
        rowId,
        DATABASE_ROW_CREATED_BY_FIELD_ID
      ),
    })
  );
}

export function writeShardedCellToLocalDoc(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown
): void {
  const { shard, shardIndex, shardDoc } = this.findWritableShardForRow(rowId);
  const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const rowMap = rowsMap.get(rowId);
  if (!isReadonlyYMap(rowMap)) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Row "${rowId}" is missing from sharded database "${this.databaseId}".`
    );
  }
  shardDoc.spaceDoc.transact(() => {
    rowMap.set(propertyId, { columnId: propertyId, value });
    this.touchShardedRowSystemFields(rowId, rowMap);
  });
  const rowIds = shardDoc.spaceDoc
    .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
    .toArray()
    .map(id => String(id));
  this.updateLocalShardEntryAtIndex(shardIndex, shard, rowIds);
  this.refreshShardedRowsFromDocs();
}

export function resolveShardedCellForWrite(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown,
  actor: DataSourceWriteActor
): RowShardCell | null {
  this.assertFileIndexCellMutationAllowed(propertyId, actor);
  const type = this.propertyTypeGet(propertyId);
  if (type == null) {
    return null;
  }
  const propertyData = this.propertyDataGet(propertyId);
  const newValue = this.normalizeSchemaLockedSelectValue(
    type,
    propertyData,
    value
  );
  if (newValue === SCHEMA_LOCKED_INVALID_CELL_VALUE) {
    return null;
  }
  const resolvedValue = this.resolveRawValueForProperty(
    rowId,
    propertyId,
    type,
    propertyData,
    newValue
  );
  return {
    columnId: propertyId,
    value: this.serializeRawValueForProperty(type, propertyData, resolvedValue),
  };
}

export function createShardedRowMap(
  this: DatabaseSourceRuntime,
  cells: RowShardCells
): YMap<unknown> {
  const rowMap = new YMap<unknown>();
  for (const [columnId, cell] of Object.entries(cells)) {
    rowMap.set(columnId, cell);
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      cells,
      DATABASE_ROW_CREATED_TIME_FIELD_ID
    ) ||
    !Object.prototype.hasOwnProperty.call(
      cells,
      DATABASE_ROW_CREATED_BY_FIELD_ID
    )
  ) {
    this.initializeShardedRowSystemFields(rowMap);
  }
  return rowMap;
}

export function copyShardedCellsByProperty(
  this: DatabaseSourceRuntime,
  fromPropertyId: string,
  toPropertyId: string
): void {
  const shards =
    normalizeDatabaseShardManifest(this._model.props.databaseShardManifest)
      ?.shards ?? [];
  for (const [shardIndex, shard] of shards.entries()) {
    const shardDoc = this.ensureShardObserved(shard, 'write');
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    shardDoc.spaceDoc.transact(() => {
      rowsMap.forEach(rawRow => {
        if (!isReadonlyYMap(rawRow) || !rawRow.has(fromPropertyId)) return;
        const cell = rawRow.get(fromPropertyId);
        rawRow.set(toPropertyId, {
          ...(cell && typeof cell === 'object' && !Array.isArray(cell)
            ? (cell as Record<string, unknown>)
            : { value: cell }),
          columnId: toPropertyId,
        });
      });
    });
    const rowIds = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(id => String(id));
    this.updateLocalShardEntryAtIndex(shardIndex, shard, rowIds);
  }
  this.refreshShardedRowsFromDocs();
}

export function writeShardedColumnCellsToLocalDocs(
  this: DatabaseSourceRuntime,
  propertyId: string,
  cells: Record<string, unknown>
): void {
  const remaining = new Set(Object.keys(cells));
  const shards =
    normalizeDatabaseShardManifest(this._model.props.databaseShardManifest)
      ?.shards ?? [];
  for (const [shardIndex, shard] of shards.entries()) {
    const shardDoc = this.ensureShardObserved(shard, 'write');
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    shardDoc.spaceDoc.transact(() => {
      for (const rowId of Array.from(remaining)) {
        const rawRow = rowsMap.get(rowId);
        if (!isReadonlyYMap(rawRow)) continue;
        rawRow.set(propertyId, {
          columnId: propertyId,
          value: cells[rowId],
        });
        this.touchShardedRowSystemFields(rowId, rawRow);
        remaining.delete(rowId);
      }
    });
    const rowIds = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(id => String(id));
    this.updateLocalShardEntryAtIndex(shardIndex, shard, rowIds);
    if (remaining.size === 0) break;
  }
  if (remaining.size > 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Rows not found in sharded database "${this.databaseId}": ${[
        ...remaining,
      ].join(', ')}`
    );
  }
  this.refreshShardedRowsFromDocs();
}

export function deleteShardedColumnFromLocalDocs(
  this: DatabaseSourceRuntime,
  propertyId: string
): void {
  for (const shard of normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  )?.shards ?? []) {
    const shardDoc = this.ensureShardObserved(shard, 'write');
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    shardDoc.spaceDoc.transact(() => {
      rowsMap.forEach(rawRow => {
        if (isReadonlyYMap(rawRow)) {
          rawRow.delete(propertyId);
        }
      });
    });
  }
  this.refreshShardedRowsFromDocs();
}

export function deleteShardedRowsFromLocalDocs(
  this: DatabaseSourceRuntime,
  rowIds: string[]
): void {
  for (const rowId of rowIds) {
    const { shard, shardIndex, shardDoc } = this.findWritableShardForRow(rowId);
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    shardDoc.spaceDoc.transact(() => {
      rowsMap.delete(rowId);
      const indexes = rowOrder
        .toArray()
        .map((candidate, index) => (String(candidate) === rowId ? index : -1))
        .filter(index => index >= 0);
      for (const index of indexes.reverse()) {
        rowOrder.delete(index, 1);
      }
    });
    this.updateLocalShardEntryAtIndex(
      shardIndex,
      shard,
      rowOrder.toArray().map(id => String(id))
    );
  }
  this.refreshShardedRowsFromDocs();
}

export function insertShardedRowIntoLocalDoc(
  this: DatabaseSourceRuntime,
  rowId: string,
  cells: RowShardCells,
  index: number
): void {
  const estimatedAdditionalBytes =
    encodedJsonBytes({ rowId, cells }) + FILE_INDEX_SHARD_WRITE_OVERHEAD_BYTES;
  const { shard, shardIndex, localIndex, shardDoc } =
    this.ensureWritableShardForInsert(index, estimatedAdditionalBytes);
  const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const rowOrder = shardDoc.spaceDoc.getArray<string>(
    DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
  );
  const rowMap = this.createShardedRowMap(cells);
  shardDoc.spaceDoc.transact(() => {
    rowsMap.set(rowId, rowMap);
    rowOrder.insert(localIndex, [rowId]);
  });
  this.updateLocalShardEntryAtIndex(
    shardIndex,
    shard,
    rowOrder.toArray().map(id => String(id))
  );
  this.refreshShardedRowsFromDocs();
}

export function rowAddWithId(
  this: DatabaseSourceRuntime,
  rowId: string,
  insertPosition: InsertToPosition | number,
  actor: DataSourceWriteActor = 'user'
): { rowId: string; created: boolean } {
  this.assertFileIndexRowMutationAllowed(actor, 'Row creation');
  if (!rowId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Cannot create database row without an id.'
    );
  }
  if (this.rowExists(rowId)) {
    return { rowId, created: false };
  }
  if (this.isRowSharded) {
    const index = this.getShardedInsertIndex(insertPosition);
    const { shard, shardIndex, localIndex, shardDoc } =
      this.ensureWritableShardForInsert(index);
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const existingRowMap = rowsMap.get(rowId);
    if (isReadonlyYMap(existingRowMap)) {
      const rowIds = rowOrder.toArray().map(id => String(id));
      if (!rowIds.includes(rowId)) {
        rowOrder.insert(localIndex, [rowId]);
        this.updateLocalShardEntryAtIndex(
          shardIndex,
          shard,
          rowOrder.toArray().map(id => String(id))
        );
        this.refreshShardedRowsFromDocs();
      }
      return { rowId, created: false };
    }
    this.insertShardedRowIntoLocalDoc(rowId, {}, index);
    return { rowId, created: true };
  }

  if (this.doc.getBlock(rowId)) {
    return { rowId, created: false };
  }
  this.assertNoLocalRootRowMutation('Row creation');
  this.doc.captureSync();
  const index =
    typeof insertPosition === 'number'
      ? insertPosition
      : insertPositionToIndex(insertPosition, this._model.children);
  this.doc.addBlock('affine:paragraph', { id: rowId }, this._model.id, index);
  touchRowMeta(this._model, rowId);
  return { rowId, created: true };
}

export function rowAddManyWithCellValues(
  this: DatabaseSourceRuntime,
  rows: readonly DatabaseRowCellValueInsert[],
  insertPosition: 'end',
  actor: DataSourceWriteActor = 'user'
): number {
  if (rows.length === 0) return 0;
  this.assertFileIndexRowMutationAllowed(actor, 'Row creation');

  if (!this.isRowSharded) {
    for (const row of rows) {
      const rowId = this.rowAddWithId(row.rowId, insertPosition, actor).rowId;
      for (const [propertyId, value] of Object.entries(row.values)) {
        this.cellValueChange(rowId, propertyId, value, actor);
      }
    }
    return rows.length;
  }

  const seenRowIds = new Set<string>();
  const newRows: Array<{ rowId: string; cells: RowShardCells }> = [];
  const existingRows: DatabaseRowCellValueInsert[] = [];

  for (const row of rows) {
    if (!row.rowId) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        'Cannot create database row without an id.'
      );
    }
    if (seenRowIds.has(row.rowId)) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Cannot create duplicate database row "${row.rowId}".`
      );
    }
    seenRowIds.add(row.rowId);

    if (this.rowExists(row.rowId)) {
      existingRows.push(row);
      continue;
    }

    const cells: RowShardCells = {};
    for (const [propertyId, value] of Object.entries(row.values)) {
      const cell = this.resolveShardedCellForWrite(
        row.rowId,
        propertyId,
        value,
        actor
      );
      if (cell) {
        cells[propertyId] = cell;
      }
    }
    newRows.push({ rowId: row.rowId, cells });
  }

  if (newRows.length > 0) {
    for (const row of newRows) {
      this.insertShardedRowIntoLocalDoc(
        row.rowId,
        row.cells,
        this.rowCount$.value
      );
    }
  }

  for (const row of existingRows) {
    this.cellValuesChange(row.rowId, row.values, actor);
  }

  return rows.length;
}

export function normalizeShardedCellValueForProperty(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown
): unknown {
  if (this.isSpacialProperty(propertyId)) {
    return this.normalizeShardedSpacialCellValue(
      rowId,
      propertyId,
      propertyId,
      value
    );
  }
  const propertyType = this.propertyTypeGet(propertyId);
  if (!propertyType) {
    return value;
  }
  if (this.isSpacialProperty(propertyType)) {
    return this.normalizeShardedSpacialCellValue(
      rowId,
      propertyId,
      propertyType,
      value
    );
  }
  const meta = this.propertyMetaGet(propertyType);
  if (!meta) {
    return value;
  }
  const converted = fromJsonValue(meta as RawValueJsonConverter, {
    data: this.propertyDataGet(propertyId),
    dataSource: this.dataSource,
    value,
  });
  const normalized = converted ?? value;
  return this.bindReactiveShardedCellValue({
    rowId,
    propertyId,
    propertyType,
    serialized: value,
    value: normalized,
  });
}

export function shardedCellKey(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): string {
  return `${rowId}\u0000${propertyId}`;
}

export function serializedShardedCellEquals(
  this: DatabaseSourceRuntime,
  left: unknown,
  right: unknown
): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left !== 'object' || typeof right !== 'object') return false;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export function bindReactiveShardedCellValue(
  this: DatabaseSourceRuntime,
  {
    propertyId,
    propertyType,
    rowId,
    serialized,
    value,
  }: {
    propertyId: string;
    propertyType: string;
    rowId: string;
    serialized: unknown;
    value: unknown;
  }
): unknown {
  const meta = this.propertyMetaGet(propertyType);
  const onUpdate = meta?.config.rawValue.onUpdate;
  if (!onUpdate || value == null) {
    return value;
  }

  const key = this.shardedCellKey(rowId, propertyId);
  const existing = this._reactiveShardedCellValues.get(key);
  if (
    existing &&
    this.serializedShardedCellEquals(existing.serialized, serialized)
  ) {
    return existing.value;
  }

  existing?.dispose();

  let initialized = false;
  const disposable = onUpdate({
    value,
    data: this.propertyDataGet(propertyId),
    dataSource: this.dataSource,
    callback: () => {
      if (!initialized) return;
      this.queueReactiveShardedCellFlush(key);
    },
  });
  initialized = true;

  this._reactiveShardedCellValues.set(key, {
    dispose: () => disposable.dispose(),
    propertyId,
    propertyType,
    rowId,
    serialized,
    value,
  });
  return value;
}

export function queueReactiveShardedCellFlush(
  this: DatabaseSourceRuntime,
  key: string
): void {
  if (this._disposed) return;
  if (this._reactiveShardedCellFrames.has(key)) return;
  const frame = requestAnimationFrame(() => {
    this._reactiveShardedCellFrames.delete(key);
    this.flushReactiveShardedCellValue(key);
  });
  this._reactiveShardedCellFrames.set(key, frame);
}

export function flushReactiveShardedCellValue(
  this: DatabaseSourceRuntime,
  key: string
): void {
  const entry = this._reactiveShardedCellValues.get(key);
  if (!entry) return;
  const propertyType = this.propertyTypeGet(entry.propertyId);
  if (!propertyType) return;
  const serialized = this.serializeRawValueForProperty(
    propertyType,
    this.propertyDataGet(entry.propertyId),
    entry.value
  );
  if (this.serializedShardedCellEquals(entry.serialized, serialized)) {
    return;
  }

  entry.serialized = serialized;
  this.writeShardedCellToLocalDoc(entry.rowId, entry.propertyId, serialized);
}

export function normalizeShardedSpacialCellValue(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  propertyType: string,
  value: unknown
): unknown {
  const meta = this.propertyMetaGet(propertyType);
  if (!meta) {
    return value;
  }
  const converted = fromJsonValue(meta as RawValueJsonConverter, {
    data: this.propertyDataGet(propertyType),
    dataSource: this.dataSource,
    value,
  });
  const normalized = converted ?? value;
  return this.bindReactiveShardedCellValue({
    rowId,
    propertyId,
    propertyType,
    serialized: value,
    value: normalized,
  });
}
